/**
 * Schichtabrechnung.jsx – Kassensturz & Tagesabrechnung
 * Mobile-First, PIN-geschützt, Multi-Standort
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  ChevronLeft, ChevronRight, Plus, Lock, Unlock, Check, X,
  Trash2, Edit2, Camera, Download, Settings, Users, Eye,
  AlertTriangle, CheckCircle2, TrendingDown, TrendingUp, RefreshCw,
  Shield, Loader2, FileText, ChevronDown, UserCheck, Pencil, ExternalLink, Copy,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SCHEIN_WERTE = [200, 100, 50, 20, 10, 5];
const TABS = ['schichten', 'abschluss', 'einstellungen', 'mitarbeiter', 'audit'];

/**
 * calcSchichtIST – Korrektes IST (Scheine + Münzgeld) für eine Schicht.
 *
 * Methode B: ist_geld = Scheine + Münzgeld → direkt verwenden
 * Methode A korrekt gespeichert (ist_geld = stueckSum): wechselgeld_ist addieren
 * Methode A falsch gespeichert (alter Bug, ist_geld = stueckSum + wg): nicht doppelt addieren
 * Heuristik: echte Schein-Summen sind immer Vielfache von 5€ (500 Cent).
 */
function calcSchichtIST(s) {
  const ist = s.ist_geld || 0;
  const wg  = s.wechselgeld_ist || 0;
  if (s.eingabe_methode === 'B') return ist;
  const wgAlreadyIn = wg > 0 && Math.round(ist * 100) % 500 !== 0;
  return wgAlreadyIn ? ist : ist + wg;
}

/** calcSchichtDiff – Korrekte Differenz = calcSchichtIST − SOLL */
function calcSchichtDiff(s) {
  return Math.round((calcSchichtIST(s) - (s.soll_geld || 0)) * 100) / 100;
}

export default function Schichtabrechnung() {
  const { user } = useAuth();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // ── State ──
  const [pinVerified, setPinVerified]   = useState(
    () => sessionStorage.getItem('kassensturz_pin_ok') === '1'
  );
  const [pinTyp, setPinTyp]             = useState('master'); // 'master' | 'temp'
  const [verifiedMitarbeiter, setVerifiedMitarbeiter] = useState(
    () => { try { return JSON.parse(sessionStorage.getItem('kassensturz_mitarb') || 'null'); } catch { return null; } }
  );
  const [pinInput, setPinInput]         = useState('');
  const [pinError, setPinError]         = useState('');
  const [pinLoading, setPinLoading]     = useState(false);
  const [mustChangePin, setMustChangePin] = useState(false); // Nach Standard-PIN → Pflicht-Änderung
  const [newPin, setNewPin]             = useState('');
  const [newPinConfirm, setNewPinConfirm] = useState('');
  const [changingPin, setChangingPin]   = useState(false);

  const [locations, setLocations]       = useState([]);
  const [selectedLoc, setSelectedLoc]   = useState('');
  // Standard: gestern (nicht heute – Abrechnung ist für den vergangenen Tag)
  const [selectedDate, setSelectedDate] = useState(() => {
    // Zuletzt betrachteten Tag merken — sonst landet man nach Seitenwechsel
    // wieder auf "gestern" und gesetzte Haken wirken verschwunden
    const saved = sessionStorage.getItem('kassensturz_selected_date');
    if (saved) return saved;
    const now = new Date();
    now.setDate(now.getDate() - 1); // gestern
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  useEffect(() => {
    if (selectedDate) sessionStorage.setItem('kassensturz_selected_date', selectedDate);
  }, [selectedDate]);
  const [activeTab, setActiveTab]       = useState('schichten');
  const [schichtFilter, setSchichtFilter] = useState('all'); // 'all' | 'mine'

  const [tagData, setTagData]           = useState(null);
  const [config, setConfig]             = useState(null);
  const [loading, setLoading]           = useState(false);

  const [showSchichtDialog, setShowSchichtDialog]       = useState(false);
  const [editingSchicht, setEditingSchicht]             = useState(null);
  const [showAbschlussEdit, setShowAbschlussEdit]       = useState(false);
  const [showFreischaltungDlg, setShowFreischaltungDlg] = useState(false);
  const [showJelaDlg, setShowJelaDlg]                  = useState(false);
  const [showZuweisenDlg, setShowZuweisenDlg]          = useState(false);
  const [zuweisenSchicht, setZuweisenSchicht]           = useState(null);
  const [showLohnabzugDlg, setShowLohnabzugDlg]        = useState(false);
  const [lohnSchicht, setLohnSchicht]                  = useState(null);

  const [employees, setEmployees]       = useState([]);
  const [freischaltungen, setFreischaltungen] = useState([]);
  const [auditLogs, setAuditLogs]       = useState([]);
  const [mitarbeiterView, setMitarbeiterView] = useState(null);
  const [jelaPasswort, setJelaPasswort] = useState('');

  const isSA    = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin' || isSA;
  const isSL    = user?.role === 'stationsleitung';

  // Config auch VOR PIN-Verifizierung laden (für korrekte "PIN gesetzt" Anzeige)
  const [preConfig, setPreConfig] = useState(null);
  useEffect(() => {
    if (selectedLoc) {
      axios.get(`${API}/api/kassensturz/config?location_id=${selectedLoc}`, { headers })
        .then(r => setPreConfig(r.data))
        .catch(() => {});
    }
  }, [selectedLoc]); // eslint-disable-line

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/user/locations`, { headers })
      .then(r => {
        setLocations(r.data || []);
        if (r.data?.length) setSelectedLoc(r.data[0].id);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    axios.get(`${API}/api/admin/employees`, { headers })
      // Nur AKTIVE Mitarbeiter in allen Auswahl-Dropdowns der Schichtabrechnung
      .then(r => setEmployees((r.data || []).filter(e => e.is_active !== false && e.is_archived !== true)))
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (pinVerified && selectedLoc) {
      fetchTag();
      fetchConfig();
    }
  }, [pinVerified, selectedLoc, selectedDate]); // eslint-disable-line

  useEffect(() => {
    if (pinVerified && selectedLoc && activeTab === 'mitarbeiter') fetchFreischaltungen();
    if (pinVerified && selectedLoc && activeTab === 'audit' && isSA)   fetchAudit();
  }, [activeTab, pinVerified, selectedLoc]); // eslint-disable-line

  const fetchTag = async () => {
    if (!selectedLoc) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/kassensturz/tag?location_id=${selectedLoc}&datum=${selectedDate}`,
        { headers }
      );
      setTagData(r.data);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally { setLoading(false); }
  };

  const fetchConfig = async () => {
    if (!selectedLoc) return;
    try {
      const r = await axios.get(
        `${API}/api/kassensturz/config?location_id=${selectedLoc}`, { headers }
      );
      setConfig(r.data);
    } catch (e) {}
  };

  const fetchFreischaltungen = async () => {
    try {
      const r = await axios.get(
        `${API}/api/kassensturz/freischaltungen?location_id=${selectedLoc}`, { headers }
      );
      setFreischaltungen(r.data || []);
    } catch (e) {}
  };

  const fetchAudit = async () => {
    try {
      const r = await axios.get(
        `${API}/api/kassensturz/audit?location_id=${selectedLoc}`, { headers }
      );
      setAuditLogs(r.data || []);
    } catch (e) {}
  };

  // ── PIN Handling ──────────────────────────────────────────────────────────
  const handlePinDigit = (d) => {
    if (pinInput.length >= 8) return;
    setPinInput(p => p + d);
    setPinError('');
  };
  const handlePinDelete = () => setPinInput(p => p.slice(0,-1));

  const handlePinVerify = async () => {
    if (!pinInput || !selectedLoc) return;
    setPinLoading(true);
    try {
      const res = await axios.post(`${API}/api/kassensturz/verify-pin`,
        { pin: pinInput, location_id: selectedLoc, typ: pinTyp }, { headers }
      );
      if (!res.data.ok) {
        setPinError(res.data.error || 'Falscher Code – bitte erneut versuchen');
        setPinInput('');
      } else if (res.data.is_default_pin) {
        setMustChangePin(true);
      } else {
        sessionStorage.setItem('kassensturz_pin_ok', '1');
        if (pinTyp === 'temp' && res.data.mitarbeiter_name) {
          const mitarb = { id: res.data.mitarbeiter_id, name: res.data.mitarbeiter_name };
          setVerifiedMitarbeiter(mitarb);
          sessionStorage.setItem('kassensturz_mitarb', JSON.stringify(mitarb));
        } else {
          setVerifiedMitarbeiter(null);
          sessionStorage.removeItem('kassensturz_mitarb');
        }
        setPinVerified(true);
      }
    } catch (e) {
      setPinError(e.response?.data?.detail || 'Verbindungsfehler – bitte erneut versuchen');
      setPinInput('');
    } finally { setPinLoading(false); }
  };

  const handleChangePinSubmit = async () => {
    if (newPin.length < 4) return toast.error('PIN muss mindestens 4 Stellen haben');
    if (newPin !== newPinConfirm) return toast.error('PINs stimmen nicht überein');
    setChangingPin(true);
    try {
      await axios.put(`${API}/api/kassensturz/config`,
        { location_id: selectedLoc, pin_master: newPin }, { headers }
      );
      toast.success('Neuer PIN gesetzt – Willkommen!');
      setMustChangePin(false);
      setNewPin(''); setNewPinConfirm('');
      sessionStorage.setItem('kassensturz_pin_ok', '1');
      setPinVerified(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setChangingPin(false); }
  };

  const handleLock = () => {
    sessionStorage.removeItem('kassensturz_pin_ok');
    sessionStorage.removeItem('kassensturz_mitarb');
    setPinVerified(false);
    setVerifiedMitarbeiter(null);
    setPinInput('');
  };

  // ── Date navigation ───────────────────────────────────────────────────────
  // Lokales heutiges Datum (nicht UTC!)
  const todayLocal = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  })();

  const changeDate = (delta) => {
    const [y, mo, d] = selectedDate.split('-').map(Number);
    const dt = new Date(y, mo - 1, d);
    dt.setDate(dt.getDate() + delta);
    const ny = dt.getFullYear();
    const nm = String(dt.getMonth() + 1).padStart(2, '0');
    const nd = String(dt.getDate()).padStart(2, '0');
    setSelectedDate(`${ny}-${nm}-${nd}`);
  };
  const fmtDate = (s) => new Date(s + 'T00:00:00').toLocaleDateString('de-DE',
    { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fmtEur = (v) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0) + '\u00a0€';
  const diffColor = (d) => !d ? '' : d > 0 ? 'text-green-600' : d < 0 ? 'text-red-600' : 'text-green-600';
  const diffBg    = (d) => !d ? '' : d > 0 ? 'bg-green-50 dark:bg-green-900/20 border-green-200' :
    d < 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200' : 'bg-green-50 dark:bg-green-900/20 border-green-200';

  // ── PDF Export ────────────────────────────────────────────────────────────
  const handlePdfExport = async () => {
    try {
      const r = await axios.get(
        `${API}/api/kassensturz/export-pdf?location_id=${selectedLoc}&datum=${selectedDate}`,
        { headers, responseType: 'blob' }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Schichtabrechnung_${selectedDate}.pdf`; a.click();
    } catch (e) {
      toast.error('PDF-Export fehlgeschlagen');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PIN OVERLAY
  // ═══════════════════════════════════════════════════════════════════════════
  if (!pinVerified) {
    const NUMS = ['1','2','3','4','5','6','7','8','9','','0','⌫'];
    return (
      <DashboardLayout>
      <div className="flex items-start justify-center p-4 pb-28 pt-2 min-h-[80vh]">
          <div className="w-full max-w-xs">
            <div className="text-center mb-6">
              <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Lock className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Schichtabrechnung</h2>
              <p className="text-sm text-muted-foreground mt-1">Stations-PIN eingeben</p>
            </div>

            {/* Standort-Auswahl */}
            {locations.length > 1 && (
              <div className="mb-4">
                <select
                  value={selectedLoc}
                  onChange={e => setSelectedLoc(e.target.value)}
                  className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm"
                >
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}

            {/* Zugangsmodus: Master-PIN vs Temp-PIN */}
            <div className="flex rounded-xl border border-border overflow-hidden mb-4">
              {[{id:'master',label:'Admin / SL',hint:'Master-PIN'},{id:'temp',label:'Mitarbeiter',hint:'Temp-PIN'}].map(t => (
                <button key={t.id} type="button"
                  onClick={() => { setPinTyp(t.id); setPinInput(''); setPinError(''); }}
                  className={`flex-1 py-2 text-xs font-medium transition-colors flex flex-col items-center ${pinTyp === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
                  <span>{t.label}</span><span className="opacity-70 font-normal">{t.hint}</span>
                </button>
              ))}
            </div>

            {/* PIN-Dots – immer genau soviele wie eingegeben, min. 4 */}
            <div className="flex justify-center gap-3 mb-6">
              {Array.from({ length: Math.max(4, pinInput.length) }).map((_, i) => (
                <div key={i} className={`w-4 h-4 rounded-full border-2 transition-colors ${
                  i < pinInput.length ? 'bg-primary border-primary' : 'border-muted-foreground/40'
                }`} />
              ))}
            </div>

            {pinError && (
              <p className="text-center text-sm text-destructive mb-4">{pinError}</p>
            )}

            {/* Numpad */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {NUMS.map((n, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => n === '⌫' ? handlePinDelete() : n ? handlePinDigit(n) : null}
                  className={`h-14 rounded-2xl text-xl font-semibold transition-all active:scale-95 ${
                    !n ? 'invisible' :
                    n === '⌫'
                      ? 'bg-muted text-foreground hover:bg-muted/80'
                      : 'bg-card border border-border text-foreground hover:bg-muted shadow-sm'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>

            <Button
              className="w-full h-12 text-base font-semibold"
              onClick={handlePinVerify}
              disabled={!pinInput || pinLoading}
            >
              {pinLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Bestätigen'}
            </Button>

            {!preConfig?.pin_gesetzt && !mustChangePin && (
              <p className="text-center text-xs text-muted-foreground mt-3">
                Standard-PIN für ersten Zugang: <strong>1234</strong>
              </p>
            )}
          </div>
        </div>

        {/* ── Pflicht-PIN-Änderung Dialog (nach Standard-PIN) ─────────── */}
        <Dialog open={mustChangePin} onOpenChange={() => {}}>
          <DialogContent className="max-w-xs" onPointerDownOutside={e => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <Shield className="h-5 w-5" />
                Neuen PIN vergeben
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 text-sm text-amber-800 dark:text-amber-300">
                Du hast den Standard-PIN <strong>1234</strong> verwendet. Bitte vergib jetzt einen persönlichen PIN.
              </div>
              <div className="space-y-1.5">
                <Label>Neuer PIN (min. 4 Stellen)</Label>
                <Input
                  inputMode="numeric" type="password" style={{ fontSize: '16px' }}
                  placeholder="Neuen PIN eingeben" value={newPin}
                  onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label>PIN bestätigen</Label>
                <Input
                  inputMode="numeric" type="password" style={{ fontSize: '16px' }}
                  placeholder="PIN wiederholen" value={newPinConfirm}
                  onChange={e => setNewPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  onKeyDown={e => e.key === 'Enter' && handleChangePinSubmit()}
                />
              </div>
              {newPin && newPinConfirm && newPin !== newPinConfirm && (
                <p className="text-xs text-destructive">PINs stimmen nicht überein</p>
              )}
            </div>
            <DialogFooter>
              <Button
                className="w-full h-12 text-base font-semibold gap-2"
                onClick={handleChangePinSubmit}
                disabled={changingPin || newPin.length < 4 || newPin !== newPinConfirm}
              >
                {changingPin && <Loader2 className="h-4 w-4 animate-spin" />}
                <Check className="h-4 w-4" />
                PIN festlegen & einloggen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  const schichten  = tagData?.schichten || [];
  const tag        = tagData?.tag || {};
  // IST gesamt + Differenz immer aus Schichten neu berechnen (inkl. Münzgeld für Methode A)
  const istGesamt  = schichten.reduce((sum, s) => sum + calcSchichtIST(s), 0);
  const sollGesamt = tagData?.soll_gesamt || 0;
  const diffGesamt = schichten.reduce((sum, s) => sum + calcSchichtDiff(s), 0);
  const kleingeld  = tagData?.kleingeld_kumuliert || 0;
  const cfg        = tagData?.config || config || {};

  // ═══════════════════════════════════════════════════════════════════════════
  // HAUPT-LAYOUT
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto p-4 space-y-4 pb-24" data-testid="schichtabrechnung-page">

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-foreground">Schichtabrechnung</h1>
            {locations.length > 1 && (
              <select
                value={selectedLoc}
                onChange={e => { setSelectedLoc(e.target.value); }}
                className="mt-1 text-sm bg-transparent text-muted-foreground border-0 outline-none cursor-pointer"
              >
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="icon" variant="ghost" onClick={handlePdfExport} title="PDF exportieren">
              <Download className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={handleLock} title="Sperren">
              <Lock className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </div>

        {/* ── Datumsnavigation ─────────────────────────────────────────── */}
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-10 w-10 shrink-0"
            onClick={() => changeDate(-1)}
            data-testid="prev-day-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>

          {/* Datum direkt klickbar / eingebbar */}
          <div className="flex-1 flex items-center gap-2 justify-center">
            <input
              type="date"
              value={selectedDate}
              max={todayLocal}
              onChange={e => e.target.value && setSelectedDate(e.target.value)}
              className="h-10 px-3 rounded-xl border border-border bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
              style={{ fontSize: '16px' }}
            />
            {selectedDate !== todayLocal && (
              <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap"
                onClick={() => setSelectedDate(todayLocal)}>
                Heute
              </Button>
            )}
          </div>

          <Button size="icon" variant="outline" className="h-10 w-10 shrink-0"
            onClick={() => changeDate(1)}
            disabled={selectedDate >= todayLocal}
            data-testid="next-day-btn"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-10 w-10 shrink-0" onClick={fetchTag}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* ── Tageszusammenfassung ──────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'IST gesamt', value: istGesamt, neutral: true },
            { label: 'SOLL gesamt', value: sollGesamt, neutral: true },
            { label: 'Differenz', value: diffGesamt, colored: true },
          ].map(({ label, value, colored }) => (
            <Card key={label} className={`text-center ${colored ? diffBg(value) : ''}`}>
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground mb-1">{label}</p>
                <p className={`text-base font-bold tabular-nums ${colored ? diffColor(value) : ''}`}>
                  {fmtEur(value)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Tabs ─────────────────────────────────────────────────────── */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 overflow-x-auto scrollbar-hide">
          {[
            { id: 'schichten',     label: 'Schichten' },
            { id: 'abschluss',     label: 'Tagesabschl.' },
            { id: 'einstellungen', label: 'Einstellungen', restrict: isAdmin || isSL },
            { id: 'mitarbeiter',   label: 'Mitarbeiter', restrict: isAdmin || isSL },
            { id: 'audit',         label: 'Audit-Log', restrict: isSA },
          ].filter(t => t.restrict !== false).map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 min-w-max px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                activeTab === t.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            TAB: SCHICHTEN
        ══════════════════════════════════════════════════════════════ */}
        {!loading && activeTab === 'schichten' && (
          <div className="space-y-3">
            {/* Filter: Alle / Nur meine Schichten */}
            {schichten.length > 0 && (
              <div className="flex rounded-xl border border-border overflow-hidden text-sm">
                {[
                  { id: 'all', label: 'Alle Schichten' },
                  { id: 'mine', label: 'Nur meine' },
                ].map(f => (
                  <button key={f.id} type="button"
                    onClick={() => setSchichtFilter(f.id)}
                    data-testid={`schicht-filter-${f.id}`}
                    className={`flex-1 py-2 font-medium transition-colors ${
                      schichtFilter === f.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                    }`}>
                    {f.label}
                  </button>
                ))}
              </div>
            )}

            {(() => {
              const angezeigt = schichtFilter === 'mine'
                ? schichten.filter(s =>
                    s.mitarbeiter_id === user?.id ||
                    s.created_by === user?.id
                  )
                : schichten;

              if (schichten.length === 0) return (
                <Card>
                  <CardContent className="py-10 text-center text-muted-foreground">
                    <p className="text-sm">Noch keine Schichten für diesen Tag</p>
                    <p className="text-xs mt-1">Klicke auf "Schicht erfassen" um zu beginnen</p>
                  </CardContent>
                </Card>
              );

              if (angezeigt.length === 0) return (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    <p className="text-sm">Keine eigenen Schichten für diesen Tag</p>
                    <button type="button" className="text-xs text-primary mt-1 underline"
                      onClick={() => setSchichtFilter('all')}>Alle anzeigen</button>
                  </CardContent>
                </Card>
              );

              return angezeigt.map(s => (
              <SchichtCard
                key={s.id}
                schicht={s}
                cfg={cfg}
                isAdmin={isAdmin}
                isSL={isSL}
                onEdit={() => { setEditingSchicht(s); setShowSchichtDialog(true); }}
                onDelete={async () => {
                  if (!window.confirm('Schicht löschen?')) return;
                  try {
                    await axios.delete(`${API}/api/kassensturz/schichten/${s.id}`, { headers });
                    toast.success('Schicht gelöscht');
                    fetchTag();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
                }}
                onEinfrieren={async () => {
                  try {
                    await axios.post(`${API}/api/kassensturz/schichten/${s.id}/einfrieren`, {}, { headers });
                    toast.success('Schicht eingefroren');
                    fetchTag();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
                }}
                onZuweisen={() => { setZuweisenSchicht(s); setShowZuweisenDlg(true); }}
                onLohnabzug={() => { setLohnSchicht(s); setShowLohnabzugDlg(true); }}
                onLohnErledigt={async () => {
                  await axios.post(`${API}/api/kassensturz/schichten/${s.id}/lohnabzug-erledigt`, {}, { headers });
                  toast.success('Als erledigt markiert');
                  fetchTag();
                }}
                headers={headers}
                onFotoUpload={fetchTag}
                fmtEur={fmtEur}
                diffColor={diffColor}
              />
            ));
            })()}

            <Button
              className="w-full gap-2 h-12"
              onClick={() => { setEditingSchicht(null); setShowSchichtDialog(true); }}
              data-testid="add-schicht-btn"
            >
              <Plus className="h-5 w-5" /> Schicht erfassen
            </Button>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            TAB: TAGESABSCHLUSS
        ══════════════════════════════════════════════════════════════ */}
        {!loading && activeTab === 'abschluss' && (
          <TagesabschlussTab
            tag={tag} cfg={cfg} schichten={schichten}
            istGesamt={istGesamt} kleingeld={kleingeld}
            selectedLoc={selectedLoc} selectedDate={selectedDate}
            headers={headers} onSaved={fetchTag}
            fmtEur={fmtEur}
          />
        )}

        {/* ══════════════════════════════════════════════════════════════
            TAB: EINSTELLUNGEN
        ══════════════════════════════════════════════════════════════ */}
        {!loading && activeTab === 'einstellungen' && (
          <EinstellungenTab
            cfg={cfg} selectedLoc={selectedLoc}
            headers={headers} onSaved={() => { fetchConfig(); fetchTag(); }}
            isAdmin={isAdmin}
          />
        )}

        {/* ══════════════════════════════════════════════════════════════
            TAB: MITARBEITER
        ══════════════════════════════════════════════════════════════ */}
        {!loading && activeTab === 'mitarbeiter' && (
          <MitarbeiterTab
            freischaltungen={freischaltungen}
            employees={employees}
            selectedLoc={selectedLoc}
            headers={headers}
            isAdmin={isAdmin}
            onRefresh={fetchFreischaltungen}
            onShowFreischaltung={() => setShowFreischaltungDlg(true)}
            onJelaClick={() => { setJelaPasswort(''); setMitarbeiterView(null); setShowJelaDlg(true); }}
            schichten={schichten}
            fmtEur={fmtEur}
          />
        )}

        {/* ══════════════════════════════════════════════════════════════
            TAB: AUDIT LOG
        ══════════════════════════════════════════════════════════════ */}
        {!loading && activeTab === 'audit' && isSA && (
          <AuditTab logs={auditLogs} onRefresh={fetchAudit} />
        )}
      </div>

      {/* ── Dialoge ──────────────────────────────────────────────────── */}
      <SchichtDialog
        open={showSchichtDialog}
        onClose={() => { setShowSchichtDialog(false); setEditingSchicht(null); }}
        editing={editingSchicht}
        cfg={cfg}
        selectedLoc={selectedLoc}
        selectedDate={selectedDate}
        todayLocal={todayLocal}
        employees={employees}
        headers={headers}
        onSaved={() => { setShowSchichtDialog(false); setEditingSchicht(null); fetchTag(); }}
        fmtEur={fmtEur}
        verifiedMitarbeiter={verifiedMitarbeiter}
      />

      <ZuweisenDialog
        open={showZuweisenDlg}
        schicht={zuweisenSchicht}
        employees={employees}
        headers={headers}
        onClose={() => { setShowZuweisenDlg(false); setZuweisenSchicht(null); }}
        onSaved={() => { setShowZuweisenDlg(false); fetchTag(); }}
      />

      <LohnabzugDialog
        open={showLohnabzugDlg}
        schicht={lohnSchicht}
        headers={headers}
        onClose={() => { setShowLohnabzugDlg(false); setLohnSchicht(null); }}
        onSaved={() => { setShowLohnabzugDlg(false); fetchTag(); }}
        fmtEur={fmtEur}
      />

      <FreischaltungDialog
        open={showFreischaltungDlg}
        employees={employees}
        selectedLoc={selectedLoc}
        headers={headers}
        onClose={() => setShowFreischaltungDlg(false)}
        onSaved={() => { setShowFreischaltungDlg(false); fetchFreischaltungen(); }}
      />

      <JelaDialog
        open={showJelaDlg}
        passwort={jelaPasswort}
        setPasswort={setJelaPasswort}
        mitarbeiterView={mitarbeiterView}
        setMitarbeiterView={setMitarbeiterView}
        selectedLoc={selectedLoc}
        headers={headers}
        onClose={() => setShowJelaDlg(false)}
        fmtEur={fmtEur}
        schichten={schichten}
        isAdmin={isAdmin}
        onAdminLohnabzug={(s) => { setLohnSchicht(s); setShowLohnabzugDlg(true); }}
      />
    </DashboardLayout>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// SCHICHT CARD
// ══════════════════════════════════════════════════════════════════════════════
function SchichtCard({ schicht: s, cfg, isAdmin, isSL, onEdit, onDelete,
                       onEinfrieren, onZuweisen, onLohnabzug, onLohnErledigt,
                       headers, onFotoUpload, fmtEur, diffColor }) {
  const [expanded, setExpanded]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const d = calcSchichtDiff(s);
  const istAnzeige = calcSchichtIST(s);
  const isNeg = d < 0;

  const handleFoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/kassensturz/schichten/${s.id}/upload-foto`,
        fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto gespeichert');
      onFotoUpload();
    } catch { toast.error('Fehler beim Hochladen'); }
    finally { setUploading(false); }
  };

  return (
    <Card className={`border-l-4 ${isNeg ? 'border-l-red-500' : 'border-l-green-500'}`}
          data-testid={`schicht-card-${s.id}`}>
      <CardContent className="p-4">
        {/* Header Row */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground">{s.schicht_name}</span>
              {s.status === 'in_pruefung' && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <Lock className="h-2.5 w-2.5" /> In Prüfung
                </Badge>
              )}
              {s.zugewiesen_an && (
                <Badge variant="destructive" className="text-xs">
                  {s.zugewiesen_an_name}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center flex-wrap gap-1">
              {s.mitarbeiter_name ? (
                <>
                  <Badge variant="secondary" className="text-xs px-2 py-0 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-0 font-medium">
                    {s.mitarbeiter_name}
                  </Badge>
                  <span className="opacity-60">Eingetragen: {s.created_by_name}</span>
                </>
              ) : (
                <span>{s.created_by_name}</span>
              )}
              <span className="opacity-60">· {new Date(s.created_at).toLocaleTimeString('de-DE', {hour:'2-digit',minute:'2-digit'})} Uhr</span>
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className={`text-lg font-bold tabular-nums ${diffColor(d)}`}>
              {d > 0 ? '+' : ''}{fmtEur(d)}
            </p>
            <p className="text-xs text-muted-foreground">Differenz</p>
          </div>
        </div>

        {/* Summary Row */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            { label: 'IST', value: istAnzeige },
            { label: 'SOLL', value: s.soll_geld },
            { label: 'Wechselgeld', value: s.wechselgeld_ist },
          ].map(({ label, value }) => (
            <div key={label} className="bg-muted/50 rounded-lg p-2 text-center">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-sm font-semibold tabular-nums">{fmtEur(value)}</p>
            </div>
          ))}
        </div>

        {/* Expand */}
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1 text-xs text-muted-foreground mt-2 hover:text-foreground"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          {expanded ? 'Weniger' : 'Details'}
        </button>

        {expanded && (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground text-xs">Abschöpfungen</span><br/>
                <span className="font-medium">{fmtEur(s.abschoepfungen)}</span></div>
            </div>

            {isNeg && s.lohnabzug_betrag != null && (
              <div className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                s.lohnabzug_erledigt ? 'bg-green-50 dark:bg-green-900/20' : 'bg-amber-50 dark:bg-amber-900/20'
              }`}>
                <span>Lohnabzug: <strong>{fmtEur(s.lohnabzug_betrag)}</strong></span>
                {s.lohnabzug_erledigt
                  ? <Badge variant="outline" className="text-xs text-green-700">✓ Erledigt</Badge>
                  : (isAdmin && (
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={onLohnErledigt}>
                      Als erledigt markieren
                    </Button>
                  ))}
              </div>
            )}

            {/* Foto */}
            {s.foto_url ? (
              <a href={s.foto_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-primary">
                <FileText className="h-3.5 w-3.5" /> Z-Bericht Foto öffnen
              </a>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors">
                <Camera className="h-3.5 w-3.5" />
                {uploading ? 'Lädt...' : 'Z-Bericht Foto hinzufügen (optional)'}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFoto} />

            {/* Action Buttons */}
            {(isAdmin || isSL) && (
              <div className="flex flex-wrap gap-2 pt-1">
                {s.status === 'offen' && (
                  <>
                    <Button size="sm" variant="outline" className="gap-1 text-xs h-8" onClick={onEdit}>
                      <Edit2 className="h-3 w-3" /> Bearbeiten
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1 text-xs h-8" onClick={onEinfrieren}>
                      <Lock className="h-3 w-3" /> Einfrieren
                    </Button>
                  </>
                )}
                {isNeg && isAdmin && (
                  <Button size="sm" variant="outline" className="gap-1 text-xs h-8" onClick={onLohnabzug}>
                    <TrendingDown className="h-3 w-3" /> Lohnabzug
                  </Button>
                )}
                {isAdmin && (
                  <Button size="sm" variant="ghost" className="gap-1 text-xs h-8 text-destructive" onClick={onDelete}>
                    <Trash2 className="h-3 w-3" /> Löschen
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// SCHICHT DIALOG (Erstellen / Bearbeiten)
// ══════════════════════════════════════════════════════════════════════════════
function SchichtDialog({ open, onClose, editing, cfg, selectedLoc, selectedDate, todayLocal,
                         headers, onSaved, fmtEur, employees, verifiedMitarbeiter }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [method, setMethod]       = useState('B');
  const [stueck, setStueck]       = useState(SCHEIN_WERTE.map(v => ({ schein: v, anzahl: 0 })));
  const [form, setForm]           = useState({
    schicht_name: '', scheine_geld: '', wechselgeld_ist: '0',
    abschoepfungen: '0', soll_geld: ''
  });
  const [saving, setSaving]       = useState(false);

  const schichten = cfg?.schichten || [];

  useEffect(() => {
    if (open) {
      if (editing) {
        setMethod(editing.eingabe_methode || 'B');
        setForm({
          schicht_name: editing.schicht_name || '',
          schicht_datum: editing.datum || selectedDate,
          mitarbeiter_name: editing.mitarbeiter_name || '',
          mitarbeiter_id: editing.mitarbeiter_id || null,
          scheine_geld: (editing.scheine_geld ?? editing.ist_geld)?.toString().replace('.', ',') ?? '',
          wechselgeld_ist: editing.wechselgeld_ist?.toString().replace('.', ',') ?? '0',
          abschoepfungen: editing.abschoepfungen?.toString().replace('.', ',') ?? '0',
          soll_geld: editing.soll_geld?.toString().replace('.', ',') ?? '',
        });
        if (editing.stueckelung?.length) {
          setStueck(SCHEIN_WERTE.map(v => {
            const found = editing.stueckelung.find(s => s.schein === v);
            return { schein: v, anzahl: found?.anzahl || 0 };
          }));
        }
      } else {
        setMethod('B');
        setForm({
          schicht_name: schichten[0]?.name || '',
          schicht_datum: selectedDate,
          mitarbeiter_name: verifiedMitarbeiter?.name || '',
          scheine_geld: '', wechselgeld_ist: '', abschoepfungen: '0', soll_geld: '',
        });
        setStueck(SCHEIN_WERTE.map(v => ({ schein: v, anzahl: 0 })));
      }
    }
  }, [open, editing]); // eslint-disable-line

  const parseNum = (v) => Number(String(v || '').replace(',', '.')) || 0;

  const stueckSum  = stueck.reduce((s, x) => s + x.schein * (Number(x.anzahl) || 0), 0);
  const istBerechnet = method === 'A'
    ? stueckSum + parseNum(form.wechselgeld_ist)   // Scheine + Münzgeld
    : parseNum(form.scheine_geld) + parseNum(form.wechselgeld_ist);
  // Formel: IST − SOLL = Differenz
  const diff = istBerechnet - parseNum(form.soll_geld);

  const handleSave = async () => {
    if (!form.schicht_name) return toast.error('Bitte Schicht wählen');
    setSaving(true);
    try {
      const payload = {
        location_id: selectedLoc,
        datum: form.schicht_datum || selectedDate,
        schicht_name: form.schicht_name,
        performed_by: form.mitarbeiter_name || undefined,
        // AP1: Mitarbeiter-Identität aus Temp-PIN-Login ODER Dropdown-Auswahl
        mitarbeiter_id: verifiedMitarbeiter?.id || form.mitarbeiter_id || null,
        mitarbeiter_name: verifiedMitarbeiter?.name || form.mitarbeiter_name || null,
        eingabe_methode: method,
        stueckelung: method === 'A' ? stueck.filter(s => s.anzahl > 0) : null,
        scheine_geld: method === 'B' ? parseNum(form.scheine_geld) : stueckSum,  // immer nur Scheine (kein Münzgeld)
        ist_geld: istBerechnet,   // Scheine + Münzgeld
        wechselgeld_ist: parseNum(form.wechselgeld_ist),
        abschoepfungen: 0,
        soll_geld: parseNum(form.soll_geld),
      };
      if (editing) {
        await axios.put(`${API}/api/kassensturz/schichten/${editing.id}`, payload, { headers });
        toast.success('Schicht aktualisiert');
      } else {
        await axios.post(`${API}/api/kassensturz/schichten`, payload, { headers });
        toast.success('Schicht gespeichert');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  // NF als inline-Funktion (NICHT als React-Component) um Re-Mount-Bug zu vermeiden
  const nf = (label, field, placeholder = '0,00', hint = null) => (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Input
        inputMode="decimal"
        type="text"
        style={{ fontSize: '16px' }}
        placeholder={placeholder}
        value={form[field]}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value.replace(/[^0-9.,]/g, '') }))}
        className="text-right font-mono"
      />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Schicht bearbeiten' : 'Schicht erfassen'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Datum der Schicht */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Abrechnungstag</Label>
            <input
              type="date"
              value={form.schicht_datum || selectedDate}
              max={todayLocal}
              onChange={e => setForm(f => ({ ...f, schicht_datum: e.target.value }))}
              className="w-full h-10 px-3 rounded-xl border border-border bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              style={{ fontSize: '16px' }}
            />
            <p className="text-xs text-muted-foreground">
              Für welchen Tag wird diese Schicht eingetragen?
            </p>
          </div>

          {/* Schicht-Name: Buttons + freies Textfeld */}
          <div className="space-y-2">
            <Label className="text-sm">Schicht</Label>
            {/* Schnellauswahl-Buttons */}
            <div className="grid grid-cols-2 gap-2">
              {schichten.map(s => (
                <button key={s.id} type="button"
                  onClick={() => setForm(f => ({ ...f, schicht_name: s.name }))}
                  className={`py-2.5 rounded-xl border text-sm font-medium transition-all ${
                    form.schicht_name === s.name
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-foreground hover:border-primary/50'
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
            {/* Freies Namensfeld */}
            <div className="flex items-center gap-2">
              <Input
                type="text"
                style={{ fontSize: '16px' }}
                placeholder="Oder eigenen Namen eingeben (optional)"
                value={form.schicht_name}
                onChange={e => setForm(f => ({ ...f, schicht_name: e.target.value }))}
              />
            </div>
          </div>

          {/* Mitarbeiter — bei Temp-PIN gesperrt, bei Master-PIN frei wählbar */}
          <div className="space-y-2">
            <Label className="text-sm">Mitarbeiter
              {verifiedMitarbeiter
                ? <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">automatisch gesetzt</span>
                : <span className="text-muted-foreground font-normal text-xs ml-1">(optional)</span>}
            </Label>
            {verifiedMitarbeiter ? (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200">
                <span className="text-sm font-semibold">{verifiedMitarbeiter.name}</span>
                <span className="text-xs text-green-600 ml-auto">Laut Temp-PIN</span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 max-h-28 overflow-y-auto">
                  {(employees || []).map(e => (
                    <button key={e.id} type="button"
                      onClick={() => setForm(f => ({ ...f, mitarbeiter_name: e.name, mitarbeiter_id: e.id }))}
                      className={`py-2 rounded-xl border text-xs font-medium transition-all truncate px-2 ${
                        form.mitarbeiter_name === e.name ? 'border-primary bg-primary/10 text-primary' : 'border-border text-foreground hover:border-primary/50'
                      }`}>{e.name}
                    </button>
                  ))}
                </div>
                <Input type="text" style={{ fontSize: '16px' }} placeholder="Oder Namen direkt eingeben"
                  value={form.mitarbeiter_name || ''}
                  onChange={e => setForm(f => ({ ...f, mitarbeiter_name: e.target.value, mitarbeiter_id: null }))} />
              </>
            )}
          </div>
          <div className="flex rounded-xl border border-border overflow-hidden">
            {[
              {id:'B', label:'Zählautomat', hint:'Endbetrag + Münzen separat'},
              {id:'A', label:'Stückelung', hint:'Scheinwerte einzeln eingeben'},
            ].map(m => (
              <button key={m.id} type="button"
                onClick={() => setMethod(m.id)}
                className={`flex-1 py-2.5 px-2 flex flex-col items-center gap-0.5 transition-colors ${
                  method === m.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                <span className="text-sm font-medium">{m.label}</span>
                <span className={`text-[10px] font-normal ${method === m.id ? 'opacity-80' : 'opacity-60'}`}>{m.hint}</span>
              </button>
            ))}
          </div>

          {/* Methode A: Stückelung */}
          {method === 'A' && (
            <div className="space-y-2 p-3 bg-muted/30 rounded-xl">
              {SCHEIN_WERTE.map((v, i) => (
                <div key={v} className="flex items-center gap-3">
                  <span className="text-sm font-mono w-16 text-right">{v} €</span>
                  <span className="text-muted-foreground text-sm">×</span>
                  <Input
                    inputMode="numeric" type="text" style={{ fontSize: '16px' }}
                    className="w-20 text-center font-mono"
                    value={stueck[i].anzahl || ''}
                    onChange={e => {
                      const v2 = Math.max(0, parseInt(e.target.value) || 0);
                      setStueck(arr => arr.map((x, j) => j === i ? { ...x, anzahl: v2 } : x));
                    }}
                  />
                  <span className="text-xs text-muted-foreground w-20 font-mono">
                    = {fmtEur(v * (stueck[i].anzahl || 0))}
                  </span>
                </div>
              ))}
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between px-1 py-1">
                  <span className="text-sm text-muted-foreground">Scheine gesamt</span>
                  <span className="font-mono font-semibold">{fmtEur(stueckSum)}</span>
                </div>
                {nf('Münzgeld (€) – Kleingeld dieser Schicht', 'wechselgeld_ist', '',
                  'Wird in den Kleingeld-Topf kumuliert')}
                <div className="flex items-center justify-between px-3 py-2 mt-1 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200">
                  <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">IST = Scheine + Münzgeld</span>
                  <span className="font-mono font-bold text-base text-blue-700 dark:text-blue-300 tabular-nums">
                    {new Intl.NumberFormat('de-DE',{minimumFractionDigits:2}).format(istBerechnet)} €
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Methode B: Scheine + Münzen getrennt */}
          {method === 'B' && (
            <div className="space-y-3">
              {nf('Scheine (€) – gezähltes Bargeld', 'scheine_geld')}
              {nf('Münzen (€) – Kleingeld dieser Schicht', 'wechselgeld_ist', '',
                'Wird automatisch in den Kleingeld-Topf kumuliert')}
              <div className="flex items-center justify-between px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200">
                <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">IST = Scheine + Münzen</span>
                <span className="text-base font-bold text-blue-700 dark:text-blue-300 tabular-nums">
                  {new Intl.NumberFormat('de-DE',{minimumFractionDigits:2}).format(istBerechnet)} €
                </span>
              </div>
            </div>
          )}

          {nf('SOLL-Geld / Z-Bericht (€)', 'soll_geld')}

          {/* Live-Differenz */}
          <div className={`flex items-center justify-between p-3 rounded-xl border font-bold ${
            diff > 0 ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800' :
            diff < 0 ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800' :
            'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20'
          }`}>
            <span>Differenz</span>
            <span className="text-lg tabular-nums">{diff > 0 ? '+' : ''}{fmtEur(diff)}</span>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-schicht-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// TAGESABSCHLUSS TAB
// ══════════════════════════════════════════════════════════════════════════════
// ── KLEINGELD-TOPF MANUELLE KORREKTUR ────────────────────────────────────────
function KleingeldKorrektur({ kleingeldHeute, datum, locationId, headers, onKorrigiert, fmtEur }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [editing, setEditing] = useState(false);
  const [neuerWert, setNeuerWert] = useState('');
  const [begruendung, setBegruendung] = useState('');
  const [saving, setSaving] = useState(false);

  const handleKorrigieren = async () => {
    if (!begruendung || begruendung.trim().length < 5) { toast.error('Begründung mind. 5 Zeichen'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/kassensturz/kleingeld-topf/korrektur`, {
        location_id: locationId, datum,
        neuer_wert: Number(String(neuerWert).replace(',', '.')) || 0,
        begruendung: begruendung.trim(),
      }, { headers });
      toast.success(`Kleingeld-Topf korrigiert: ${fmtEur(r.data.alter_wert)} → ${fmtEur(r.data.neuer_wert)}`);
      setEditing(false); setNeuerWert(''); setBegruendung('');
      onKorrigiert();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border bg-muted/40 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Münzgeld heute (aus Schichten)</p>
          <p className="text-xs text-muted-foreground">Automatisch akkumuliert aus Schicht-Eingaben</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-base">{fmtEur(kleingeldHeute)}</span>
          <button type="button" onClick={() => { setNeuerWert(kleingeldHeute.toFixed(2).replace('.', ',')); setEditing(e => !e); }}
            className="p-1 rounded text-muted-foreground hover:text-primary transition-colors" title="Manuell korrigieren">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {editing && (
        <div className="space-y-2 pt-1 border-t">
          <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
            ⚠ Korrektur wird im Audit-Log erfasst
          </p>
          <div className="flex gap-2">
            <input type="text" inputMode="decimal" placeholder={kleingeldHeute.toFixed(2)}
              value={neuerWert} onChange={e => setNeuerWert(e.target.value)}
              className="flex-1 rounded-lg border px-3 py-1.5 text-sm font-mono bg-background"
              style={{ fontSize: '16px' }} />
          </div>
          <textarea rows={2} placeholder="Begründung (Pflicht, min. 5 Zeichen) — z.B. 'Tresor nachgezählt, war 12,40 €'"
            value={begruendung} onChange={e => setBegruendung(e.target.value)}
            className="w-full rounded-lg border px-3 py-1.5 text-sm bg-background resize-none"
            style={{ fontSize: '16px' }} />
          <div className="flex gap-2">
            <button type="button" onClick={() => setEditing(false)}
              className="flex-1 py-2 rounded-lg border text-xs text-muted-foreground hover:bg-muted">Abbrechen</button>
            <button type="button" onClick={handleKorrigieren} disabled={saving}
              className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50">
              {saving ? 'Speichert...' : 'Korrektur speichern'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function TagesabschlussTab({ tag, cfg, schichten, istGesamt, kleingeld,
                             selectedLoc, selectedDate, headers, onSaved, fmtEur }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const agl = cfg?.agentur_label || 'Agenturgeld';
  const dsrLabel = cfg?.dsr_nummer_label || 'DSR-Nummer';
  const einzTyp = cfg?.einzahlungs_typ || 'A';
  const [form, setForm] = useState({
    agenturgeld: '', agenturgeld_erledigt: false,
    dsr_nummer: '', kleingeld_in_schein: '',
    bank_einzahlung_real: '', bankeinzahlung_erledigt: false,
    abschlussbericht_gesellschaft: '', notizen: ''
  });
  const [saving, setSaving] = useState(false);

  const kleingeldHeute = tag?.kleingeld_manuell || 0;

  const bankVorschlag = React.useMemo(() => {
    const scheineAusSchichten = (schichten || []).reduce((sum, s) => {
      const ist = s.ist_geld || 0;
      const wg  = s.wechselgeld_ist || 0;
      if (s.eingabe_methode === 'B') {
        // Methode B: ist_geld = Scheine + Münzgeld → immer subtrahieren
        return sum + ist - wg;
      }
      // Methode A: ist_geld SOLL nur Scheine sein (stueckSum).
      // Bei leerem stueckelung-Array schlägt der Backend-Override fehl →
      // ist_geld enthält dann stueckSum + wechselgeld_ist.
      // Erkennung: echte Note-Summen (Vielfache von 5€) haben ist_geld % 500 === 0.
      // Hat ist_geld Cent-Reste die kein Schein-Vielfaches sind, steckt Münzgeld drin.
      if (wg > 0 && Math.round(ist * 100) % 500 !== 0) return sum + ist - wg;
      return sum + ist;
    }, 0);
    const klInSchein = parseFloat((form.kleingeld_in_schein || '').replace(',', '.')) || 0;
    return Math.round((scheineAusSchichten + klInSchein) * 100) / 100;
  }, [schichten, form.kleingeld_in_schein]);

  useEffect(() => {
    const kleingeldInSchein = tag?.kleingeld_in_schein?.toString() || '';
    const klInScheinNum = parseFloat(kleingeldInSchein.replace(',', '.')) || 0;
    const scheineAusSchichten = (schichten || []).reduce((sum, s) => {
      const ist = s.ist_geld || 0;
      const wg  = s.wechselgeld_ist || 0;
      if (s.eingabe_methode === 'B') return sum + ist - wg;
      if (wg > 0 && Math.round(ist * 100) % 500 !== 0) return sum + ist - wg;
      return sum + ist;
    }, 0);
    const vorschlag = Math.round((scheineAusSchichten + klInScheinNum) * 100) / 100;
    const n2de = (v) => v != null && v !== '' ? Number(v).toFixed(2).replace('.', ',') : '';
    setForm({
      agenturgeld: n2de(tag?.agenturgeld),
      agenturgeld_erledigt: tag?.agenturgeld_erledigt || false,
      dsr_nummer: tag?.dsr_nummer || '',
      kleingeld_in_schein: n2de(tag?.kleingeld_in_schein),
      bank_einzahlung_real: tag?.bank_einzahlung_real != null
        ? n2de(tag.bank_einzahlung_real)
        : (vorschlag > 0 ? vorschlag.toFixed(2).replace('.', ',') : ''),
      bankeinzahlung_erledigt: tag?.bankeinzahlung_erledigt || false,
      abschlussbericht_gesellschaft: n2de(tag?.abschlussbericht_gesellschaft),
      notizen: tag?.notizen || '',
    });
  }, [tag, schichten]);

  const parseNum = (v) => Number(String(v || '').replace(',', '.')) || 0;
  const agenturgeld = parseNum(form.agenturgeld);
  const geschaeft   = istGesamt - agenturgeld;
  const klTotal     = kleingeld + kleingeldHeute - parseNum(form.kleingeld_in_schein);
  let   agentGer    = agenturgeld;
  let   bRest       = 0;
  if (einzTyp === 'B' && agenturgeld > 0) {
    const rund = cfg?.rundung_ab || 5;
    agentGer = Math.floor(agenturgeld / rund) * rund;
    bRest    = agenturgeld - agentGer;
  }
  const bankReal = parseNum(form.bank_einzahlung_real);
  const abschluss = parseNum(form.abschlussbericht_gesellschaft);
  const diffEinzahlung = abschluss > 0 ? bankReal - abschluss : null;

  // Ansatz A (Punkt 3): Helper für mobile-freundliches Label-über-Feld-Layout
  const mf = (label, key, hint, extraContent) => (
    <div className="space-y-1">
      <Label className="text-sm font-medium block">{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {extraContent}
      <input
        type="text" inputMode="decimal" style={{ fontSize: '16px' }}
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono text-right"
        value={form[key] || ''}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      />
    </div>
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const toNum = (v) => { const n = Number(String(v || '').replace(',', '.')); return isNaN(n) ? null : n; };
      await axios.put(`${API}/api/kassensturz/tagesabschluss`, {
        location_id: selectedLoc, datum: selectedDate,
        agenturgeld:                  toNum(form.agenturgeld),
        agenturgeld_erledigt:         form.agenturgeld_erledigt,
        dsr_nummer:                   form.dsr_nummer || null,
        kleingeld_in_schein:          toNum(form.kleingeld_in_schein),
        bank_einzahlung_real:         toNum(form.bank_einzahlung_real),
        bankeinzahlung_erledigt:      form.bankeinzahlung_erledigt,
        abschlussbericht_gesellschaft: toNum(form.abschlussbericht_gesellschaft),
        notizen: form.notizen || null,
      }, { headers });
      toast.success('Tagesabschluss gespeichert');
      onSaved();
    } catch (e) { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  // Erledigt-Toggle-Helper
  const ErledtToggle = ({ field, label }) => (
    <label className="flex items-center gap-2 cursor-pointer mt-1">
      <input type="checkbox" checked={form[field] || false}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.checked }))}
        className="rounded accent-green-600" />
      <span className={`text-xs font-medium ${form[field] ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}`}>
        {form[field] ? `✓ ${label}` : label}
      </span>
    </label>
  );

  const EUR = ({ label, value, colored, highlight }) => (
    <div className={`flex items-center justify-between py-2.5 px-3 rounded-lg ${
      highlight ? 'bg-primary/5 border border-primary/20' : 'bg-muted/30'
    }`}>
      <span className={`text-sm ${highlight ? 'font-semibold' : 'text-muted-foreground'}`}>{label}</span>
      <span className={`font-mono font-bold ${colored && value < 0 ? 'text-red-600' : colored && value > 0 ? 'text-green-600' : ''}`}>
        {fmtEur(value)}
      </span>
    </div>
  );

  // nf als inline-Funktion (NICHT als React-Component) um Re-Mount-Bug zu vermeiden
  const nf = (label, field, hint = null) => (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <Input
        inputMode="decimal" type="text" style={{ fontSize: '16px' }}
        placeholder="0,00" className="text-right font-mono"
        value={form[field]}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value.replace(/[^0-9.,]/g,'') }))}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Übersicht */}
      <Card>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm font-semibold">Tagesübersicht</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <EUR label="IST gesamt (Summe Schichten)" value={istGesamt} />
          <EUR label={`${agl}-Einzahlung`} value={agenturgeld} />
          {einzTyp === 'B' && agenturgeld > 0 && (
            <div className="ml-3 space-y-1">
              <EUR label="Automat (gerundet)" value={agentGer} />
              <EUR label="→ Kleingeld-Topf (Rest)" value={bRest} />
            </div>
          )}
          <EUR label="Geschäftskonto (errechnet)" value={geschaeft} highlight />
        </CardContent>
      </Card>

      {/* Eingaben */}
      <Card>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm font-semibold">Eingaben</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Agenturgeld + DSR-Nummer — Ansatz A: Label oben, volle Breite */}
          <div className="space-y-1">
            <Label className="text-sm font-medium block">{agl}</Label>
            {einzTyp === 'B' && <p className="text-xs text-muted-foreground">Automat nimmt ab {cfg?.rundung_ab || 5} € – Rest geht in Kleingeld-Topf</p>}
            <div className="flex items-center gap-1.5">
              <input type="text" inputMode="decimal" style={{ fontSize: '16px' }}
                className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono text-right"
                value={form.agenturgeld}
                onChange={e => setForm(f => ({ ...f, agenturgeld: e.target.value }))} />
              <button type="button"
                onClick={() => { navigator.clipboard?.writeText(form.agenturgeld || '').then(() => toast.success('Kopiert!')).catch(() => toast.error('Kopieren fehlgeschlagen')); }}
                className="flex-shrink-0 h-9 w-9 rounded-lg border border-border bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                title="In Zwischenablage kopieren" data-testid="copy-agenturgeld-btn">
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <ErledtToggle field="agenturgeld_erledigt" label="Agenturgeld überwiesen" />
          </div>

          <div className="space-y-1">
            <Label className="text-sm font-medium block">{dsrLabel} <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
            <div className="flex items-center gap-1.5">
              <input type="text" style={{ fontSize: '16px' }}
                className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={`${dsrLabel} eingeben...`}
                value={form.dsr_nummer}
                onChange={e => setForm(f => ({ ...f, dsr_nummer: e.target.value }))} />
              <button type="button"
                onClick={() => { navigator.clipboard?.writeText(form.dsr_nummer || '').then(() => toast.success('Kopiert!')).catch(() => toast.error('Kopieren fehlgeschlagen')); }}
                disabled={!form.dsr_nummer}
                className="flex-shrink-0 h-9 w-9 rounded-lg border border-border bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                title="In Zwischenablage kopieren" data-testid="copy-dsr-btn">
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Kleingeld — heute automatisch aus Schicht-Münzgeld, mit manueller Korrektur-Option */}
          <KleingeldKorrektur
            kleingeldHeute={kleingeldHeute}
            datum={selectedDate}
            locationId={selectedLoc}
            headers={headers}
            onKorrigiert={onSaved}
            fmtEur={fmtEur}
          />

          {nf('Kleingeld in Scheine gewechselt (€)', 'kleingeld_in_schein', 'Wird vom kumulierten Topf abgezogen')}

          {/* Kleingeld-Topf (tagesübergreifend) */}
          <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2.5">
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Kleingeld-Topf kumuliert</p>
              <p className="text-xs text-amber-600 dark:text-amber-400">Aufgelaufenes Münzgeld im Safe (über alle Tage)</p>
            </div>
            <span className="font-mono font-bold text-amber-800 dark:text-amber-300 text-lg">
              {fmtEur(klTotal)}
            </span>
          </div>

          {nf('Soll-Betrag laut Tagesbericht (€)', 'abschlussbericht_gesellschaft',
            'Offizieller BAR-Wert aus dem digitalen Abschlussbericht der Gesellschaft')}

          {/* Gesamt Einzahlung: vorausgefüllt, aber weiterhin editierbar — Ansatz A */}
          <div className="space-y-1">
            <Label className="text-sm font-medium block">Gesamt Einzahlung laut Beleg (€)</Label>
            {bankVorschlag > 0 && form.bank_einzahlung_real !== bankVorschlag.toFixed(2).replace('.', ',') && (
              <button type="button"
                onClick={() => setForm(f => ({ ...f, bank_einzahlung_real: bankVorschlag.toFixed(2).replace('.', ',') }))}
                className="text-[10px] text-primary underline block">
                Vorschlag: {new Intl.NumberFormat('de-DE',{minimumFractionDigits:2}).format(bankVorschlag)} €
              </button>
            )}
            <p className="text-xs text-muted-foreground">
              Scheine aus allen Schichten{(parseFloat(form.kleingeld_in_schein) || 0) > 0 ? ' + Kleingeld in Scheine' : ''}
            </p>
            <input type="text" inputMode="decimal" style={{ fontSize: '16px' }}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono text-right"
              placeholder={bankVorschlag > 0 ? new Intl.NumberFormat('de-DE',{minimumFractionDigits:2}).format(bankVorschlag) : '0,00'}
              value={form.bank_einzahlung_real}
              onChange={e => setForm(f => ({ ...f, bank_einzahlung_real: e.target.value }))} />
            <ErledtToggle field="bankeinzahlung_erledigt" label="Gesamteinzahlung erledigt" />
          </div>

          {/* Differenz Einzahlung = Gesamt Einzahlung − Soll laut Tagesbericht */}
          {form.bank_einzahlung_real && abschluss > 0 && (
            <div className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold ${
              Math.abs(diffEinzahlung) < 0.01 ? 'bg-green-50 dark:bg-green-900/20 text-green-700' :
              diffEinzahlung < 0 ? 'bg-red-50 dark:bg-red-900/20 text-red-700' :
              'bg-blue-50 dark:bg-blue-900/20 text-blue-700'
            }`}>
              <span>Differenz Einzahlung</span>
              <span className="tabular-nums">{diffEinzahlung > 0 ? '+' : ''}{fmtEur(diffEinzahlung)}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-sm">Notizen (optional)</Label>
            <textarea
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Besonderheiten des Tages..."
              value={form.notizen}
              onChange={e => setForm(f => ({ ...f, notizen: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <Button className="w-full h-12 gap-2 font-semibold" onClick={handleSave} disabled={saving || tag?.is_locked}>
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        {tag?.is_locked ? 'Tag ist gesperrt (kein Speichern möglich)' : 'Tagesabschluss speichern'}
      </Button>

      {/* Tag-Lock Bereich */}
      <TagLockPanel tag={tag} datum={selectedDate} locationId={selectedLoc} headers={headers} onUpdated={onSaved} isMaster={true} />
    </div>
  );
}


// ── TAG-LOCK-PANEL ────────────────────────────────────────────────────────────
function TagLockPanel({ tag, datum, locationId, headers, onUpdated }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [showUnlock, setShowUnlock] = useState(false);
  const [pin, setPin] = useState('');
  const [begruendung, setBegruendung] = useState('');
  const [loading, setLoading] = useState(false);

  const lock = async () => {
    const p = prompt('Master-PIN zum Sperren des Tages eingeben:');
    if (!p) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/kassensturz/tag/lock`, { location_id: locationId, datum, pin: p }, { headers });
      toast.success('Tag gesperrt — keine weiteren Änderungen möglich');
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setLoading(false); }
  };

  const unlock = async () => {
    if (!pin || !begruendung.trim()) return toast.error('PIN und Begründung erforderlich');
    setLoading(true);
    try {
      await axios.post(`${API}/api/kassensturz/tag/unlock`, { location_id: locationId, datum, pin, begruendung }, { headers });
      toast.success('Tag wieder geöffnet');
      setShowUnlock(false); setPin(''); setBegruendung('');
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-2">
      {!tag?.is_locked ? (
        <button type="button" onClick={lock} disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/10 text-amber-800 dark:text-amber-300 text-sm font-medium hover:bg-amber-100 transition-colors">
          <Lock className="h-4 w-4" />
          Tag abschließen (sperren)
        </button>
      ) : (
        <div className="rounded-xl border border-green-300 bg-green-50 dark:bg-green-900/20 p-3 space-y-2">
          <p className="text-sm font-semibold text-green-800 dark:text-green-300 flex items-center gap-2">
            <Lock className="h-4 w-4" /> Tag gesperrt — {tag.locked_by_name && `von ${tag.locked_by_name}`}
          </p>
          {!showUnlock ? (
            <button type="button" onClick={() => setShowUnlock(true)}
              className="text-xs text-green-700 underline hover:no-underline">
              Tag wieder öffnen (mit Master-PIN + Begründung)
            </button>
          ) : (
            <div className="space-y-2">
              <input type="password" placeholder="Master-PIN" value={pin} onChange={e => setPin(e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm bg-background" />
              <textarea placeholder="Begründung (Pflicht)" value={begruendung} onChange={e => setBegruendung(e.target.value)}
                rows={2} className="w-full rounded-lg border px-3 py-2 text-sm bg-background resize-none" />
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowUnlock(false)}
                  className="flex-1 py-2 rounded-lg border text-xs">Abbrechen</button>
                <button type="button" onClick={unlock} disabled={loading}
                  className="flex-1 py-2 rounded-lg bg-green-700 text-white text-xs font-medium">
                  {loading ? 'Öffne...' : 'Tag öffnen'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// EINSTELLUNGEN TAB
// ══════════════════════════════════════════════════════════════════════════════
function EinstellungenTab({ cfg, selectedLoc, headers, onSaved, isAdmin }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({
    pin_master: '', pin_confirm: '',
    agentur_label: cfg?.agentur_label || 'Agenturgeld',
    dsr_nummer_label: cfg?.dsr_nummer_label || 'DSR-Nummer',
    differenz_warnschwelle: cfg?.differenz_warnschwelle?.toString() || '5',
    einzahlungs_typ: cfg?.einzahlungs_typ || 'A',
    rundung_ab: cfg?.rundung_ab?.toString() || '5',
    sl_pin: cfg?.sl_rechte?.pin_aendern || false,
    sl_schichten: cfg?.sl_rechte?.schichten_konfigurieren || false,
    sl_freischalten: cfg?.sl_rechte?.mitarbeiter_freischalten || false,
  });
  const [schichten, setSchichten] = useState(cfg?.schichten || []);
  const [newSchicht, setNewSchicht] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (cfg) {
      setForm(f => ({
        ...f,
        agentur_label: cfg.agentur_label || 'Agenturgeld',
        dsr_nummer_label: cfg.dsr_nummer_label || 'DSR-Nummer',
        differenz_warnschwelle: cfg.differenz_warnschwelle?.toString() || '5',
        einzahlungs_typ: cfg.einzahlungs_typ || 'A',
        rundung_ab: cfg.rundung_ab?.toString() || '5',
        sl_pin: cfg.sl_rechte?.pin_aendern || false,
        sl_schichten: cfg.sl_rechte?.schichten_konfigurieren || false,
        sl_freischalten: cfg.sl_rechte?.mitarbeiter_freischalten || false,
      }));
      setSchichten(cfg.schichten || []);
    }
  }, [cfg]);

  const handleSave = async () => {
    if (form.pin_master && form.pin_master !== form.pin_confirm)
      return toast.error('PINs stimmen nicht überein');
    setSaving(true);
    try {
      await axios.put(`${API}/api/kassensturz/config`, {
        location_id: selectedLoc,
        schichten,
        ...(form.pin_master ? { pin_master: form.pin_master } : {}),
        einzahlungs_typ: form.einzahlungs_typ,
        agentur_label: form.agentur_label,
        dsr_nummer_label: form.dsr_nummer_label,
        differenz_warnschwelle: Number(form.differenz_warnschwelle) || 5,
        rundung_ab: Number(form.rundung_ab) || 5,
        sl_rechte: {
          pin_aendern: form.sl_pin,
          schichten_konfigurieren: form.sl_schichten,
          mitarbeiter_freischalten: form.sl_freischalten,
        },
      }, { headers });
      toast.success('Einstellungen gespeichert');
      setForm(f => ({ ...f, pin_master: '', pin_confirm: '' }));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  const Toggle = ({ label, field }) => (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm">{label}</span>
      <button type="button"
        onClick={() => setForm(f => ({ ...f, [field]: !f[field] }))}
        className={`w-11 h-6 rounded-full transition-colors relative ${form[field] ? 'bg-primary' : 'bg-muted-foreground/30'}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form[field] ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </label>
  );

  return (
    <div className="space-y-4">
      {/* PIN */}
      <Card>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Stations-PIN {cfg?.pin_gesetzt ? '(gesetzt)' : '(noch nicht gesetzt)'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-sm">{cfg?.pin_gesetzt ? 'Neue PIN' : 'PIN festlegen'}</Label>
            <Input inputMode="numeric" type="password" style={{ fontSize: '16px' }}
              placeholder="4-8 Stellen"
              value={form.pin_master}
              onChange={e => setForm(f => ({ ...f, pin_master: e.target.value.replace(/\D/,'') }))}
            />
          </div>
          {form.pin_master && (
            <div className="space-y-1.5">
              <Label className="text-sm">PIN wiederholen</Label>
              <Input inputMode="numeric" type="password" style={{ fontSize: '16px' }}
                placeholder="PIN bestätigen"
                value={form.pin_confirm}
                onChange={e => setForm(f => ({ ...f, pin_confirm: e.target.value.replace(/\D/,'') }))}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schichten */}
      <Card>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm font-semibold">Schichtmodell</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {schichten.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <Input value={s.name} style={{ fontSize: '16px' }}
                onChange={e => setSchichten(arr => arr.map((x, j) => j===i ? {...x, name: e.target.value} : x))}
                className="flex-1"
              />
              <Button size="icon" variant="ghost" className="text-destructive"
                onClick={() => setSchichten(arr => arr.filter((_,j) => j !== i))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input placeholder="Neue Schicht..." value={newSchicht} style={{ fontSize: '16px' }}
              onChange={e => setNewSchicht(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newSchicht.trim()) {
                setSchichten(arr => [...arr, { id: Date.now().toString(), name: newSchicht.trim(), sort_order: arr.length }]);
                setNewSchicht('');
              }}}
            />
            <Button variant="outline" onClick={() => {
              if (!newSchicht.trim()) return;
              setSchichten(arr => [...arr, { id: Date.now().toString(), name: newSchicht.trim(), sort_order: arr.length }]);
              setNewSchicht('');
            }}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Kassenstock & Typ */}
      <Card>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-sm font-semibold">Abrechnung</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm">Label Agenturgeld-Feld</Label>
            <Input type="text" style={{ fontSize: '16px' }}
              placeholder="z.B. DSR-Nummer, Agenturgeld..."
              value={form.agentur_label}
              onChange={e => setForm(f => ({ ...f, agentur_label: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Label Referenznummer-Feld</Label>
            <Input type="text" style={{ fontSize: '16px' }}
              placeholder="z.B. DSR-Nummer, Berichts-Nr., Ref.-Nr. ..."
              value={form.dsr_nummer_label}
              onChange={e => setForm(f => ({ ...f, dsr_nummer_label: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Einzahlungs-Modus</Label>
            {[
              { id: 'A', label: 'Typ A – Die Überweiser', desc: 'Gesamtes Bargeld auf Geschäftskonto, danach Überweisung an Gesellschaft' },
              { id: 'B', label: 'Typ B – Die Trenner', desc: `Agenturgeld separat am Automaten bar einzahlen. Rest-Cents gehen in Kleingeld-Topf` },
            ].map(t => (
              <button key={t.id} type="button"
                onClick={() => setForm(f => ({ ...f, einzahlungs_typ: t.id }))}
                className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                  form.einzahlungs_typ === t.id ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <p className="text-sm font-medium">{t.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t.desc}</p>
              </button>
            ))}
          </div>

          {form.einzahlungs_typ === 'B' && (
            <div className="space-y-1.5">
              <Label className="text-sm">Automat nimmt ab (€) – Rundungsgrenze</Label>
              <Input inputMode="decimal" type="text" style={{ fontSize: '16px' }}
                value={form.rundung_ab} placeholder="5"
                onChange={e => setForm(f => ({ ...f, rundung_ab: e.target.value }))}
                className="font-mono text-right"
              />
            </div>
          )}

          {/* Differenz-Warnschwelle */}
          <div className="space-y-1.5">
            <Label className="text-sm">Differenz-Warnschwelle (€) – Push-Benachrichtigung</Label>
            <Input inputMode="decimal" type="text" style={{ fontSize: '16px' }}
              value={form.differenz_warnschwelle} placeholder="5"
              onChange={e => setForm(f => ({ ...f, differenz_warnschwelle: e.target.value }))}
              className="font-mono text-right"
            />
            <p className="text-xs text-muted-foreground">Bei |Differenz| über ±{form.differenz_warnschwelle || 5} € → Push an Admin/SL</p>
          </div>

        </CardContent>
      </Card>

      {/* SL-Rechte */}
      {isAdmin && (
        <Card>
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-sm font-semibold">Stationsleitung-Berechtigungen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Toggle label="PIN ändern darf SL" field="sl_pin" />
            <Toggle label="Schichten konfigurieren darf SL" field="sl_schichten" />
            <Toggle label="Mitarbeiter freischalten darf SL" field="sl_freischalten" />
          </CardContent>
        </Card>
      )}

      <Button className="w-full h-12 font-semibold" onClick={handleSave} disabled={saving}>
        {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        Einstellungen speichern
      </Button>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// MITARBEITER TAB
// ══════════════════════════════════════════════════════════════════════════════
function MitarbeiterTab({ freischaltungen, employees, selectedLoc, headers, isAdmin,
                          onRefresh, onShowFreischaltung, onJelaClick, schichten, fmtEur }) {
  const today = new Date().toISOString().slice(0,10);
  const aktive = freischaltungen.filter(f => f.datum_von <= today && f.datum_bis >= today && f.is_active);
  const API = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-4">
      {/* Kassenminus Übersicht */}
      {schichten.filter(s => (s.differenz || 0) < 0).length > 0 && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/10">
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-sm font-semibold text-red-700 dark:text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Kassenminus heute
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {schichten.filter(s => (s.differenz || 0) < 0).map(s => (
              <div key={s.id} className="flex items-center justify-between text-sm">
                <span>{s.schicht_name} {s.zugewiesen_an_name ? `→ ${s.zugewiesen_an_name}` : ''}</span>
                <span className="font-mono font-bold text-red-600">{fmtEur(s.differenz)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Mitarbeiter-Ansicht → ersetzt durch "Meine Abrechnung" (neue Seite) */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Mitarbeiter-Abrechnungen</p>
              <p className="text-xs text-muted-foreground">
                Mitarbeiter können ihre freigegebenen Monatsabrechnungen auf ihrer eigenen Seite einsehen.
              </p>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5"
              onClick={() => window.open('/meine-abrechnung', '_blank')}>
              <ExternalLink className="h-3.5 w-3.5" /> Meine Abrechnung
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Abrechnungen freigeben: Aufgaben → Reporting → Mitarbeiter-Minus
          </p>
        </CardContent>
      </Card>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// AUDIT TAB
// ══════════════════════════════════════════════════════════════════════════════
function AuditTab({ logs, onRefresh }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Änderungs-Protokoll</p>
        <Button size="sm" variant="ghost" onClick={onRefresh}>
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>
      {logs.length === 0 && (
        <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">
          Noch keine Protokolleinträge
        </CardContent></Card>
      )}
      {logs.map(l => (
        <Card key={l.id} className="border-l-4 border-l-violet-400">
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-semibold">{l.typ}</p>
                <p className="text-xs text-muted-foreground">{l.feld}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium">{l.wer_name}</p>
                <p className="text-xs text-muted-foreground">{new Date(l.timestamp).toLocaleString('de-DE')}</p>
              </div>
            </div>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-red-600 line-through max-w-[45%] truncate">{l.wert_vorher}</span>
              <span className="text-muted-foreground">→</span>
              <span className="text-green-600 max-w-[45%] truncate">{l.wert_nachher}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// DIALOGE
// ══════════════════════════════════════════════════════════════════════════════
function ZuweisenDialog({ open, schicht, employees, headers, onClose, onSaved }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [sel, setSel] = useState('');
  const [saving, setSaving] = useState(false);
  const emp = employees.filter(e => e.role === 'employee' || e.role === 'stationsleitung' || e.role === 'admin');

  // Vorausfüllen mit dem Mitarbeiter der bereits an der Schicht hinterlegt ist
  useEffect(() => {
    if (open && schicht) {
      setSel(schicht.mitarbeiter_id || schicht.zugewiesen_an || '');
    }
  }, [open, schicht]);

  const handleSave = async () => {
    const e = emp.find(x => x.id === sel); if (!e) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/kassensturz/schichten/${schicht.id}/zuweisen`,
        { mitarbeiter_id: e.id, mitarbeiter_name: e.name }, { headers });
      toast.success(`${schicht?.schicht_name} an ${e.name} zugewiesen`);
      onSaved();
    } catch (e) { toast.error('Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Kassenminus zuweisen</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Schicht: <strong>{schicht?.schicht_name}</strong> · {schicht?.datum}<br/>
          Differenz: <strong className="text-red-600">{(schicht?.differenz || 0).toFixed(2)} €</strong>
        </p>
        <select value={sel} onChange={e => setSel(e.target.value)}
          style={{ fontSize: '16px' }}
          className="w-full h-11 rounded-xl border border-border bg-background px-3 text-sm">
          <option value="">Mitarbeiter wählen...</option>
          {emp.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={!sel || saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Zuweisen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LohnabzugDialog({ open, schicht, headers, onClose, onSaved, fmtEur }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [betrag, setBetrag] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && schicht) {
      setBetrag(Math.abs(schicht.differenz || 0).toFixed(2));
    }
  }, [open, schicht]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/kassensturz/schichten/${schicht.id}/lohnabzug`,
        { betrag: Number(betrag) || 0 }, { headers });
      toast.success('Lohnabzug gespeichert');
      onSaved();
    } catch { toast.error('Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Lohnabzug festlegen</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Kassenminus: <strong className="text-red-600">{fmtEur(schicht?.differenz)}</strong>
        </p>
        <div className="space-y-1.5">
          <Label>Lohnabzug Betrag (€)</Label>
          <Input inputMode="decimal" type="text" style={{ fontSize: '16px' }}
            value={betrag} placeholder="0,00"
            onChange={e => setBetrag(e.target.value.replace(/[^0-9.,]/g,''))}
            className="font-mono text-right text-lg"
          />
          <p className="text-xs text-muted-foreground">
            Der Rest ({fmtEur((schicht?.differenz || 0) + Number(betrag) * -1)}) wird als erledigt gebucht
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FreischaltungDialog({ open, employees, selectedLoc, headers, onClose, onSaved }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const today = new Date().toISOString().slice(0,10);
  const [form, setForm] = useState({
    mitarbeiter_id: '', pin_temp: '', mitarbeiter_passwort: '',
    datum_von: today, datum_bis: today,
  });
  const [saving, setSaving] = useState(false);
  const emp = employees.filter(e => e.role === 'employee' || e.role === 'stationsleitung' || e.role === 'admin');

  const handleSave = async () => {
    const e = emp.find(x => x.id === form.mitarbeiter_id);
    if (!e || !form.pin_temp || !form.mitarbeiter_passwort) return toast.error('Alle Felder ausfüllen');
    setSaving(true);
    try {
      await axios.post(`${API}/api/kassensturz/freischaltungen`, {
        location_id: selectedLoc, mitarbeiter_id: e.id, mitarbeiter_name: e.name,
        pin_temp: form.pin_temp, mitarbeiter_passwort: form.mitarbeiter_passwort,
        datum_von: form.datum_von, datum_bis: form.datum_bis,
      }, { headers });
      toast.success('Freischaltung erstellt');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Mitarbeiter freischalten</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Mitarbeiter</Label>
            <select value={form.mitarbeiter_id}
              onChange={e => setForm(f => ({ ...f, mitarbeiter_id: e.target.value }))}
              style={{ fontSize: '16px' }}
              className="w-full h-11 rounded-xl border border-border bg-background px-3 text-sm">
              <option value="">Wählen...</option>
              {emp.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Von</Label>
              <Input type="date" style={{ fontSize: '16px' }} value={form.datum_von}
                onChange={e => setForm(f => ({ ...f, datum_von: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Bis</Label>
              <Input type="date" style={{ fontSize: '16px' }} value={form.datum_bis}
                onChange={e => setForm(f => ({ ...f, datum_bis: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Temporäre PIN (für Mohammad-Zugang)</Label>
            <Input inputMode="numeric" type="password" style={{ fontSize: '16px' }}
              placeholder="4-8 Stellen" value={form.pin_temp}
              onChange={e => setForm(f => ({ ...f, pin_temp: e.target.value.replace(/\D/g,'') }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Mitarbeiter-Passwort (für Jela-Ansicht)</Label>
            <Input type="password" style={{ fontSize: '16px' }}
              placeholder="Beliebiges Passwort" value={form.mitarbeiter_passwort}
              onChange={e => setForm(f => ({ ...f, mitarbeiter_passwort: e.target.value }))} />
            <p className="text-xs text-muted-foreground">Mit diesem Passwort öffnet der Mitarbeiter seine persönliche Kassenminus-Ansicht</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Erstellen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JelaDialog({ open, passwort, setPasswort, mitarbeiterView, setMitarbeiterView,
                      selectedLoc, headers, onClose, fmtEur, schichten, isAdmin, onAdminLohnabzug }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const handleVerify = async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${API}/api/kassensturz/mitarbeiter-ansicht`,
        { passwort, location_id: selectedLoc }, { headers });
      setMitarbeiterView(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Falsches Passwort');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); setMitarbeiterView(null); setPasswort(''); }}>
      <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" /> Mitarbeiter-Ansicht
          </DialogTitle>
        </DialogHeader>

        {!mitarbeiterView ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Persönliches Passwort eingeben um eigene Fehlbeträge zu sehen
            </p>
            <Input type="password" style={{ fontSize: '16px' }}
              placeholder="Passwort" value={passwort}
              onChange={e => setPasswort(e.target.value)} />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button className="w-full" onClick={handleVerify} disabled={!passwort || loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Ansicht öffnen
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-center">
                <p className="text-xs text-muted-foreground">Gesamt-Minus</p>
                <p className="text-lg font-bold text-red-600">{fmtEur(mitarbeiterView.gesamt_minus)}</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center">
                <p className="text-xs text-muted-foreground">Lohnabzug</p>
                <p className="text-lg font-bold text-amber-700">{fmtEur(mitarbeiterView.gesamt_lohnabzug)}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center">Diesen Monat · Nur deine Fehlbeträge</p>

            {mitarbeiterView.fehlbetraege.length === 0 ? (
              <div className="text-center py-4">
                <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Keine Fehlbeträge diesen Monat!</p>
              </div>
            ) : mitarbeiterView.fehlbetraege.map((f, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-border">
                <div>
                  <p className="text-sm font-medium">{f.schicht_name}</p>
                  <p className="text-xs text-muted-foreground">{f.datum}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-red-600">{fmtEur(f.differenz)}</p>
                  {f.lohnabzug_betrag != null && (
                    <p className="text-xs text-muted-foreground">Abzug: {fmtEur(f.lohnabzug_betrag)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
